import { useSyncExternalStore } from 'react';
import type { CliEnvelope, WireOutcome } from '@makan/cloud';

/**
 * The browser's client for the cloud: the derived command and query routes,
 * and staff sign-in. The page never builds a URL beyond these. `@makan/cloud`
 * is imported for its types only (rules R08, R16, E05).
 * The one file in the app that calls `fetch`.
 *
 * A command id is minted per tap and kept for the retry (`newCommandId`, held
 * by the caller until an answer arrives), so a double tap or a dropped
 * connection never issues a command twice.
 *
 * `command` and `show` ride out a restart of the cloud or its database: a
 * call that gets no answer we understand (no connection, a gateway error
 * without an envelope, or for a command COMMAND_ATTEMPT_TIMEOUT_MS with
 * nothing back) is sent again, a command under the same command id, after
 * 0.5, 1, 2, 4, then every 8 s, for up to RETRY_FOR_MS. A read has no
 * per-attempt timeout: a slow one completes rather than being run again. The cloud answers a repeated command id from its
 * outcome log, so a retry of a command that did land is not a second one.
 * While any call waits to retry, `useReconnecting` is true and the page shows
 * the kit's `Reconnecting` line; past RETRY_FOR_MS the call throws Offline
 * and the page says so.
 */

/** The cloud could not be reached, or did not answer with something we understand. */
export class Offline extends Error {
  override readonly name = 'Offline';
}

export type { WireOutcome };

const jsonHeaders = { 'content-type': 'application/json' };

async function call(path: string, init?: RequestInit, timeoutMs?: number): Promise<Response> {
  try {
    return await fetch(path, { ...init, credentials: 'same-origin', ...(timeoutMs !== undefined && { signal: AbortSignal.timeout(timeoutMs) }) });
  } catch (error) {
    throw new Offline(error instanceof Error ? error.message : String(error));
  }
}

/** How long a command or a read keeps trying before it throws Offline: a database restart or a Cloud SQL maintenance takes under a minute. */
export const RETRY_FOR_MS = 60_000;
/** One command attempt with nothing back by then counts as unanswered and is sent again. */
const COMMAND_ATTEMPT_TIMEOUT_MS = 30_000;
const BACKOFF_MS = [500, 1_000, 2_000, 4_000, 8_000] as const;

let waiting = 0;
const listeners = new Set<() => void>();
const waitingChanged = (by: number) => {
  waiting += by;
  for (const listener of listeners) listener();
};

/** Whether a command or a read is waiting to retry: what the kit's `Reconnecting` line shows. */
export function useReconnecting(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => waiting > 0,
    () => false,
  );
}

async function retrying<T>(attempt: () => Promise<T>): Promise<T> {
  const started = Date.now();
  let counted = false;
  try {
    for (let tries = 0; ; tries += 1) {
      try {
        return await attempt();
      } catch (error) {
        const wait = BACKOFF_MS[Math.min(tries, BACKOFF_MS.length - 1)] ?? 8_000;
        if (!(error instanceof Offline) || Date.now() - started + wait > RETRY_FOR_MS) throw error;
        if (!counted) waitingChanged(1);
        counted = true;
        await new Promise((resolve) => setTimeout(resolve, wait));
      }
    }
  } finally {
    if (counted) waitingChanged(-1);
  }
}

async function outcomeOf(response: Response): Promise<WireOutcome> {
  const body = (await response.json().catch(() => undefined)) as Partial<CliEnvelope> | undefined;
  if (body?.result === undefined) throw new Offline(`the cloud answered ${String(response.status)} without an envelope`);
  return body.result;
}

/** Who a call is from when it is not the page's session: the member holding this member pass. */
export interface AsMember {
  readonly pass: string;
}

/** The bound station a call comes through: staff signed in on a cashier station, or a voucher station by itself. */
export interface AtStation {
  readonly station: string;
}

const asHeaders = (as?: AsMember | AtStation): Record<string, string> => {
  if (as === undefined) return {};
  return 'pass' in as ? { authorization: `Pass ${as.pass}` } : { station: as.station };
};

/** POST /api/g/:groupId/:feature/:command with the tap's command id, retried under it until answered (RETRY_FOR_MS). */
export function command(groupId: string, feature: string, name: string, body: unknown, commandId: string, as?: AsMember | AtStation): Promise<WireOutcome> {
  const init = { method: 'POST', headers: { ...jsonHeaders, 'command-id': commandId, ...asHeaders(as) }, body: JSON.stringify(body) };
  return retrying(async () => outcomeOf(await call(`/api/g/${encodeURIComponent(groupId)}/${feature}/${name}`, init, COMMAND_ATTEMPT_TIMEOUT_MS)));
}

/** GET /api/g/:groupId/:feature/show/:projection?field=value, retried until answered (RETRY_FOR_MS). */
export function show(groupId: string, feature: string, projection: string, query: Readonly<Record<string, string | number | undefined>> = {}, as?: AsMember | AtStation): Promise<WireOutcome> {
  const params = new URLSearchParams();
  for (const [field, value] of Object.entries(query)) if (value !== undefined) params.set(field, String(value));
  const search = params.size > 0 ? `?${params.toString()}` : '';
  return retrying(async () => outcomeOf(await call(`/api/g/${encodeURIComponent(groupId)}/${feature}/show/${projection}${search}`, { headers: asHeaders(as) })));
}

/** What `uploadMedia` answers: the stored medium's id, or why the cloud refused it (HTTP status). */
export type MediaUpload = { readonly ok: true; readonly mediaId: string; readonly contentType: string; readonly bytes: number } | { readonly ok: false; readonly status: number };

/** POST /api/g/:groupId/:feature/media/:slot with the raw bytes: the cloud checks the type and the size (cloud/src/http/media.ts). */
export async function uploadMedia(groupId: string, feature: string, slot: string, media: Blob): Promise<MediaUpload> {
  const response = await call(`/api/g/${encodeURIComponent(groupId)}/${feature}/media/${encodeURIComponent(slot)}`, { method: 'POST', headers: { 'content-type': media.type }, body: media });
  if (response.status !== 201) return { ok: false, status: response.status };
  const body = (await response.json().catch(() => undefined)) as { mediaId?: string; contentType?: string; bytes?: number } | undefined;
  if (body?.mediaId === undefined) throw new Offline('the upload answered without a media id');
  return { ok: true, mediaId: body.mediaId, contentType: body.contentType ?? media.type, bytes: body.bytes ?? media.size };
}

/** Where anyone's browser reads a public slot's medium (an offer's picture): an <img> src, no sign-in. */
export function publicMediaUrl(groupId: string, feature: string, slot: string, mediaId: string): string {
  return `/api/g/${encodeURIComponent(groupId)}/${feature}/media/${encodeURIComponent(slot)}/${encodeURIComponent(mediaId)}`;
}

/** Where a manager's browser reads a stored medium: an <img> or <video> src (the cloud answers the bytes, or redirects to a short-lived link). */
export function mediaUrl(groupId: string, feature: string, mediaId: string): string {
  return `/api/g/${encodeURIComponent(groupId)}/${feature}/media/${encodeURIComponent(mediaId)}`;
}

export interface SignedIn {
  readonly staffId: string;
  readonly email: string;
  readonly groupId: string;
  readonly role: 'manager' | 'cashier';
  /** Where the cloud runs (MAKAN_ENV): local, test, staging or production. */
  readonly environment: 'local' | 'test' | 'staging' | 'production';
  /** The `<feature>/<command>` routes this role may issue, from the session: a page offers only those. */
  readonly commands: readonly string[];
  /** The `<feature>/<projection>` this role may list in full; another it reads only by naming one row, or not at all. */
  readonly lists: readonly string[];
}

/** Whether the signed-in staff may issue this command (its route name, like `end-campaign`): a page hides what it may not. */
export const canIssue = (staff: SignedIn | null, feature: string, command: string): boolean => staff !== null && staff.commands.includes(`${feature}/${command}`);

/** Whether the signed-in staff may list this projection in full: a page hides the lists it may not. */
export const canList = (staff: SignedIn | null, feature: string, projection: string): boolean => staff !== null && staff.lists.includes(`${feature}/${projection}`);

export type SignInAnswer = { readonly ok: true; readonly staff: SignedIn } | { readonly ok: false; readonly reason: 'wrong' } | { readonly ok: false; readonly reason: 'rate limited'; readonly retryAfterSeconds: number };

export async function signIn(email: string, password: string): Promise<SignInAnswer> {
  const response = await call('/api/auth/sign-in', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ email, password }) });
  if (response.status === 429) {
    const outcome = await outcomeOf(response);
    return { ok: false, reason: 'rate limited', retryAfterSeconds: outcome.outcome === 'rate limited' ? outcome.retryAfterSeconds : 60 };
  }
  if (response.status === 401) return { ok: false, reason: 'wrong' };
  const body = (await response.json().catch(() => undefined)) as { staff?: SignedIn } | undefined;
  if (!response.ok || body?.staff === undefined) throw new Offline(`sign-in answered ${String(response.status)}`);
  remember(true);
  held({ state: 'signed in', staff: body.staff });
  return { ok: true, staff: body.staff };
}

export async function signOut(): Promise<void> {
  await call('/api/auth/sign-out', { method: 'POST' });
  remember(false);
  held({ state: 'reading' });
}

/** The signed-in staff, or null. */
export async function currentStaff(): Promise<SignedIn | null> {
  const response = await call('/api/auth/session');
  if (response.status === 401) {
    remember(false);
    held({ state: 'signed out' });
    return null;
  }
  const body = (await response.json().catch(() => undefined)) as { staff?: SignedIn } | undefined;
  if (!response.ok || body?.staff === undefined) throw new Offline(`session answered ${String(response.status)}`);
  remember(true);
  held({ state: 'signed in', staff: body.staff });
  return body.staff;
}

/**
 * The staff session as this tab last heard it: read once, then kept while
 * pages change, so moving between staff pages never waits on it again.
 * Every answer about it (sign-in, sign-out, each currentStaff) updates it;
 * `readStaffSession` asks again in the background.
 */
export type StaffSession = { readonly state: 'reading' } | { readonly state: 'signed out' } | { readonly state: 'offline' } | { readonly state: 'signed in'; readonly staff: SignedIn };

let session: StaffSession = { state: 'reading' };
const sessionListeners = new Set<() => void>();

const sameStaff = (a: SignedIn, b: SignedIn): boolean =>
  a.staffId === b.staffId && a.email === b.email && a.groupId === b.groupId && a.role === b.role && a.environment === b.environment && a.commands.join() === b.commands.join() && a.lists.join() === b.lists.join();

/** Holds the session; an answer that says what is already held changes nothing, so no page draws again for it. */
function held(next: StaffSession): void {
  if (next.state === session.state && (next.state !== 'signed in' || (session.state === 'signed in' && sameStaff(next.staff, session.staff)))) return;
  session = next;
  for (const listener of sessionListeners) listener();
}

export function useStaffSession(): StaffSession {
  return useSyncExternalStore(
    (listener) => {
      sessionListeners.add(listener);
      return () => sessionListeners.delete(listener);
    },
    () => session,
    () => session,
  );
}

/** Asks the cloud for the session again, answering it as now held (useStaffSession hears it too). Unreachable, a session already held is kept: the page stays, and its own calls say offline. */
export async function readStaffSession(): Promise<StaffSession> {
  try {
    await currentStaff();
  } catch (error) {
    if (!(error instanceof Offline)) throw error;
    if (session.state !== 'signed in') held({ state: 'offline' });
  }
  return session;
}

/**
 * Where this browser notes that staff signed in on it. The session cookie is
 * httpOnly, so a page cannot see it; without this note every customer page
 * would ask the cloud for a staff session and get a 401.
 */
const STAFF_HERE = 'makan.staff-here';

/** Whether staff signed in on this browser and have not been seen signed out since: only then is a staff session worth asking for. Without storage, it cannot tell, so it says yes. */
export function staffSignedInHere(): boolean {
  try {
    return localStorage.getItem(STAFF_HERE) !== null;
  } catch {
    return true;
  }
}

function remember(signedIn: boolean): void {
  try {
    if (signedIn) localStorage.setItem(STAFF_HERE, '1');
    else localStorage.removeItem(STAFF_HERE);
  } catch {
    // No storage: staffSignedInHere says yes, so nothing is lost.
  }
}

/**
 * A UUIDv7 (the runtime refuses any other command id): 48 bits of
 * milliseconds, version 7, the variant, the rest random. `getRandomValues`
 * works on plain http too, where `randomUUID` does not.
 */
export function newCommandId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let ms = Date.now();
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  bytes[6] = 0x70 | ((bytes[6] ?? 0) & 0x0f);
  bytes[8] = 0x80 | ((bytes[8] ?? 0) & 0x3f);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A member pass for this browser: 32 random bytes as base64url (43 characters), made here and sent once; the cloud keeps only its hash. */
/** A fresh secret for a member pass or a station binding token: 32 random bytes, base64url. */
export function newSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

/** GET /api/host: the group whose own host this page opened on, or null on the product's (cloud/src/http/hosts.ts). */
export async function hostGroupOf(): Promise<string | null> {
  const response = await call('/api/host');
  const body = (await response.json().catch(() => undefined)) as { groupId?: string | null } | undefined;
  return response.ok ? (body?.groupId ?? null) : null;
}
